import React, { useState, useMemo, useEffect } from 'react';
import { PlusCircle, Filter, LayoutGrid, List } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';

// --- IMPORTAÇÃO DE COMPONENTES ---
import PendencyCard from '../common/cards/PendencyCard';
import PendencyListItem from '../common/PendencyListItem';
import Pagination from '../common/Pagination';
import SearchInput from '../common/SearchInput';
// --- NOVO COMPONENTE DE STATUS IMPORTADO ---
import PendencyStatusDisplay from '../pendencies/PendencyStatusDisplay';

/**
 * @description Componente de página para exibir e gerenciar a lista de pendências.
 * @param {object} props - As propriedades passadas para o componente.
 * @param {Array} props.enrichedPendencies - A lista de pendências já com dados do criador e responsável.
 * @param {object} props.userData - Dados do usuário logado para verificação de permissões.
 * @param {function} props.onOpenPendencyModal - Função para abrir o modal de pendência.
 * @param {boolean} props.showOnlyPending - Estado que controla se apenas pendentes são exibidas.
 * @param {function} props.setShowOnlyPending - Função para alterar o estado showOnlyPending.
 * @param {Array} props.pendencyStatusChartData - Dados para o gráfico de status de pendências.
 */
const PendenciesPage = ({ 
    enrichedPendencies, 
    userData, 
    onOpenPendencyModal, 
    showOnlyPending, 
    setShowOnlyPending, 
    pendencyStatusChartData,
    settings,
    onTogglePendencyPin,
    usersMap
}) => {
    // --- ESTADOS LOCAIS DA PÁGINA ---
    const [searchTerm, setSearchTerm] = useState('');
    const [viewMode, setViewMode] = useState('cards'); // 'cards' ou 'list'
    const [currentPage, setCurrentPage] = useState(1);
    const PENDENCIES_PER_PAGE = 15;

    // ⚡ Bolt: Use a Set for O(1) pinned status lookups during filtering, instead of O(N) Array.includes()
    const pinnedPendencies = useMemo(() => new Set(settings?.pinnedPendencies || []), [settings?.pinnedPendencies]);

    // --- MEMOIZAÇÃO PARA FILTRAGEM E ORDENAÇÃO ---
    const filteredPendencies = useMemo(() => {
        // Optimized: Uses normalized string for robust matching with the pre-calculated searchString
        const normalizedSearch = searchTerm ? searchTerm.toString().toLowerCase().normalize("NFD").replace(/[\u0300-\u036f]/g, "").trim() : "";
        // Priority values are now pre-calculated in enrichedPendencies as priorityValue (Alta=3, Média=2, Baixa=1)

        return enrichedPendencies
            .filter(pendency => {
                // Optimized: Removed redundant showOnlyPending check as enrichedPendencies is already filtered

                // Adicionado filtro para técnicos de campo não verem pendências administrativas
                if (userData?.role === 'tecnico_campo' && pendency.tipo === 'Administrativa') {
                    return false;
                }

                if (!normalizedSearch) {
                    return true;
                }

                // Check the pre-calculated search string
                return pendency.searchString && pendency.searchString.includes(normalizedSearch);
            })
            .sort((a, b) => {
                // Optimized: Use pre-computed values for faster sorting
                // Priority: Higher value first (3 > 2 > 1)
                const priorityA = a.priorityValue || 0;
                const priorityB = b.priorityValue || 0;
                if (priorityA !== priorityB) {
                    return priorityB - priorityA;
                }

                // Date: Oldest first (Ascending timestamp)
                // Use pre-computed timestamp (number)
                const dateA = a.dateTimestamp || 32503680000000;
                const dateB = b.dateTimestamp || 32503680000000;
                return dateA - dateB;
            });
    }, [enrichedPendencies, searchTerm, userData]);

    const { pinned, unpinned } = useMemo(() => {
        const pinned = [];
        const unpinned = [];
        filteredPendencies.forEach(p => {
            if (pinnedPendencies.has(p.id)) {
                pinned.push(p);
            } else {
                unpinned.push(p);
            }
        });
        return { pinned, unpinned };
    }, [filteredPendencies, pinnedPendencies]);
    
    const displayPendencies = useMemo(() => [...pinned, ...unpinned], [pinned, unpinned]);

    // --- CONTAGEM DE PENDÊNCIAS PENDENTES PARA O TÍTULO ---
    const pendingCount = useMemo(() => {
        return enrichedPendencies.filter(p => p.status === 'Pendente').length;
    }, [enrichedPendencies]);

    // --- LÓGICA DE PAGINAÇÃO ---
    const totalPages = Math.ceil(displayPendencies.length / PENDENCIES_PER_PAGE);
    const paginatedPendencies = useMemo(() => {
        const startIndex = (currentPage - 1) * PENDENCIES_PER_PAGE;
        const endIndex = startIndex + PENDENCIES_PER_PAGE;
        return displayPendencies.slice(startIndex, endIndex);
    }, [currentPage, displayPendencies]);

    useEffect(() => {
        setCurrentPage(1);
    }, [searchTerm, showOnlyPending, pinnedPendencies]);

    const canCreate = userData?.role === 'administrador' || userData?.role === 'tecnico';
    const canPin = userData?.role === 'administrador' || userData?.role === 'tecnico';

    return (
        <div className="p-4">
            {/* Cabeçalho e Controles de Filtro */}
            <div className="flex flex-col sm:flex-row justify-between items-center mb-4 gap-4">
                {/* --- TÍTULO ATUALIZADO COM A CONTAGEM --- */}
                <h2 className="text-2xl font-bold text-slate-700 dark:text-slate-200">
                    Pendências <span className="text-slate-400 font-medium">({pendingCount})</span>
                </h2>
                <div className="flex items-center gap-x-2 w-full sm:w-auto">
                    {/* Botões de modo de visualização */}
                    <div className="flex items-center p-1 bg-slate-200 dark:bg-slate-700 rounded-lg">
                        <button
                            onClick={() => setViewMode('cards')}
                            title="Visualização em Cards"
                            className={`p-1.5 rounded-md transition-colors ${viewMode === 'cards' ? 'bg-white dark:bg-slate-600 text-blue-600 dark:text-blue-300' : 'text-slate-500 dark:text-slate-400'}`}
                        >
                            <LayoutGrid className="h-5 w-5" />
                        </button>
                        <button
                            onClick={() => setViewMode('list')}
                            title="Visualização em Lista"
                            className={`p-1.5 rounded-md transition-colors ${viewMode === 'list' ? 'bg-white dark:bg-slate-600 text-blue-600 dark:text-blue-300' : 'text-slate-500 dark:text-slate-400'}`}
                        >
                            <List className="h-5 w-5" />
                        </button>
                    </div>
                    <div className="flex-grow">
                        <SearchInput
                            value={searchTerm}
                            onChange={(e) => setSearchTerm(e.target.value)}
                            placeholder="Buscar por ID, cidade, descrição..."
                            className="w-full"
                        />
                    </div>
                    <button
                        onClick={() => setShowOnlyPending(!showOnlyPending)}
                        title={showOnlyPending ? "Mostrando apenas pendentes" : "Mostrando todas"}
                        className={`p-2 rounded-lg transition-colors ${showOnlyPending ? 'bg-blue-100 text-blue-600 dark:bg-blue-900/50 dark:text-blue-300' : 'bg-slate-200 dark:bg-slate-700'}`}
                    >
                        <Filter className="h-5 w-5" />
                    </button>
                    {canCreate && (
                        <button
                            onClick={() => onOpenPendencyModal()}
                            className="btn-primary flex items-center shrink-0"
                        >
                            <PlusCircle className="mr-2 h-5 w-5" /> Nova
                        </button>
                    )}
                </div>
            </div>

            {/* --- NOVA BARRA DE STATUS DE PENDÊNCIAS --- */}
            <div className="mb-6">
                <PendencyStatusDisplay data={pendencyStatusChartData} />
            </div>

            {/* Renderização Condicional: Cards ou Lista */}
            {viewMode === 'cards' ? (
                <motion.div layout className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                    <AnimatePresence>
                        {paginatedPendencies.length > 0 ? (
                            paginatedPendencies.map(pendency => (
                                <PendencyCard
                                    key={pendency.id}
                                    pendency={pendency}
                                    onCardClick={onOpenPendencyModal}
                                    creatorUser={usersMap?.get(pendency.cadastradoPor?.uid)}
                                    assignedUser={usersMap?.get(pendency.executadoPor?.uid)}
                                    isPinned={pinnedPendencies.has(pendency.id)}
                                    onPinClick={onTogglePendencyPin}
                                    canPin={canPin}
                                />
                            ))
                        ) : (
                            <p className="text-center text-slate-500 dark:text-slate-400 py-8 col-span-full">
                                Nenhuma pendência encontrada.
                            </p>
                        )}
                    </AnimatePresence>
                </motion.div>
            ) : (
                <div className="space-y-2 mb-4">
                    {/* Cabeçalho da Lista (visível apenas em telas maiores) */}
                    <div className="hidden md:grid grid-cols-12 gap-4 w-full items-center px-3 py-2 text-xs font-bold text-slate-500 dark:text-slate-400 uppercase">
                        <div className="col-span-1">ID</div>
                        <div className="col-span-4">Descrição</div>
                        <div className="col-span-1">Cidade</div>
                        <div className="col-span-1">Prioridade</div>
                        <div className="col-span-2">Tempo Decorrido</div>
                        <div className="col-span-1 text-center">Data</div>
                        <div className="col-span-2 text-right">Usuários</div>
                    </div>
                    {/* Corpo da Lista */}
                    <AnimatePresence>
                        {paginatedPendencies.length > 0 ? (
                            paginatedPendencies.map(pendency => (
                                <PendencyListItem
                                    key={pendency.id}
                                    pendency={pendency}
                                    onCardClick={onOpenPendencyModal}
                                    creatorUser={usersMap?.get(pendency.cadastradoPor?.uid)}
                                    assignedUser={usersMap?.get(pendency.executadoPor?.uid)}
                                    isPinned={pinnedPendencies.has(pendency.id)}
                                    onPinClick={onTogglePendencyPin}
                                    canPin={canPin}
                                />
                            ))
                        ) : (
                            <p className="text-center text-slate-500 dark:text-slate-400 py-8 col-span-full">
                                Nenhuma pendência encontrada.
                            </p>
                        )}
                    </AnimatePresence>
                </div>
            )}

            {/* Controles de Paginação */}
            <Pagination
                currentPage={currentPage}
                totalPages={totalPages}
                onPageChange={setCurrentPage}
            />
        </div>
    );
};

export default PendenciesPage;
